'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Shape = 'slab' | 'column';
type BagSize = 40 | 60 | 80;

// Standard US ready-mix bag yields (Quikrete and equivalents publish these figures).
const BAG_YIELD_CU_FT: Record<BagSize, number> = { 40: 0.3, 60: 0.45, 80: 0.6 };

function useFireOnce(slug: string) {
  const fired = useRef(false);
  return () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage(slug, '').catch(() => undefined);
  };
}

export default function BrowserConcreteCalculator() {
  const fireUsage = useFireOnce('concrete-calculator');
  const [shape, setShape] = useState<Shape>('slab');
  const [lengthFt, setLengthFt] = useState(10);
  const [widthFt, setWidthFt] = useState(10);
  const [depthIn, setDepthIn] = useState(4);
  const [diameterFt, setDiameterFt] = useState(1);
  const [heightFt, setHeightFt] = useState(4);
  const [bagSize, setBagSize] = useState<BagSize>(60);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const cuFt =
      shape === 'slab'
        ? lengthFt * widthFt * (depthIn / 12)
        : Math.PI * (diameterFt / 2) ** 2 * heightFt;
    const cuYd = cuFt / 27;
    const cuM = cuFt * 0.0283168;
    const bags = Math.ceil(cuFt / BAG_YIELD_CU_FT[bagSize]);
    return { cuFt, cuYd, cuM, bags };
  }, [shape, lengthFt, widthFt, depthIn, diameterFt, heightFt, bagSize]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Concrete</span>
        <h2 className="tool-live-title">Concrete calculator</h2>
        <p className="tool-live-subtitle">
          Enter the dimensions of a slab, footing, or round column to get the concrete volume and
          how many bags of ready-mix you need.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field tool-live-field-full">
            <span>Shape</span>
            <select onChange={(e) => onChange(setShape)(e.target.value as Shape)} value={shape}>
              <option value="slab">Slab or footing (rectangular)</option>
              <option value="column">Round column or post hole</option>
            </select>
          </label>

          {shape === 'slab' ? (
            <>
              <label className="tool-live-field">
                <span>Length (ft)</span>
                <input
                  min={0}
                  onChange={(e) => onChange(setLengthFt)(Number(e.target.value) || 0)}
                  step={0.1}
                  type="number"
                  value={lengthFt}
                />
              </label>
              <label className="tool-live-field">
                <span>Width (ft)</span>
                <input
                  min={0}
                  onChange={(e) => onChange(setWidthFt)(Number(e.target.value) || 0)}
                  step={0.1}
                  type="number"
                  value={widthFt}
                />
              </label>
              <label className="tool-live-field">
                <span>Depth (in)</span>
                <input
                  min={0}
                  onChange={(e) => onChange(setDepthIn)(Number(e.target.value) || 0)}
                  step={0.5}
                  type="number"
                  value={depthIn}
                />
              </label>
            </>
          ) : (
            <>
              <label className="tool-live-field">
                <span>Diameter (ft)</span>
                <input
                  min={0}
                  onChange={(e) => onChange(setDiameterFt)(Number(e.target.value) || 0)}
                  step={0.1}
                  type="number"
                  value={diameterFt}
                />
              </label>
              <label className="tool-live-field">
                <span>Height / depth (ft)</span>
                <input
                  min={0}
                  onChange={(e) => onChange(setHeightFt)(Number(e.target.value) || 0)}
                  step={0.1}
                  type="number"
                  value={heightFt}
                />
              </label>
            </>
          )}

          <label className="tool-live-field">
            <span>Bag size</span>
            <select
              onChange={(e) => onChange(setBagSize)(Number(e.target.value) as BagSize)}
              value={bagSize}
            >
              <option value={40}>40 lb bag</option>
              <option value={60}>60 lb bag</option>
              <option value={80}>80 lb bag</option>
            </select>
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.cuYd.toFixed(2)} cubic yards</strong>
            <span>
              {result.cuFt.toFixed(1)} cubic feet, {result.cuM.toFixed(2)} cubic meters
            </span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.bags} bags</strong>
            <span>of {bagSize} lb ready-mix concrete</span>
          </p>
          <p className="tool-live-panel-note">
            Add 5-10% extra for spillage, uneven subgrade, and waste. Ready-mix bag yields are
            manufacturer figures and can vary slightly by brand; check the bag if precision matters.
          </p>
        </section>
      </div>
    </div>
  );
}
